<script setup>
import DocumentQuestionsStep from "./DocumentQuestionsStep.vue";

import axios from "axios";
import { useStore } from "vuex";

import { useIsMobile } from "@/utils/checkMobile";
import useGTM from "@/utils/gtm";

const emit = defineEmits(["completeWizard", "noActionTaken"]);
const { isAiAssistantOpen } = defineProps(["isAiAssistantOpen"]);

const store = useStore();

const buttonREF = {
  next: ref(null),
  save: ref(null),
};

const answers = computed(() => store.state.editor.wizard.answers);
const steps = computed(() => store.state.editor.wizard.steps);
const currentStep = ref(null); // Don't initialize to 0 - let restoration logic handle it
const focusedOptionIndex = ref(-1); // Start with no focus to avoid auto-highlighting first option

// Keyboard navigation
const handleKeyPress = (event) => {
  if (!steps.value[currentStep.value]) return;

  const currentStepData = steps.value[currentStep.value];
  const questions = currentStepData.subSteps.flatMap(subStep => subStep.questions);

  if (questions.length === 0) return;

  const question = questions[0]; // Assuming one question per step for simplicity
  const processedOptions = getProcessedOptions(question);

  switch(event.key) {
    case '1':
    case '2':
    case '3':
    case '4':
    case '5':
      const optionIndex = parseInt(event.key) - 1;
      if (processedOptions[optionIndex]) {
        selectOption(question.id, processedOptions[optionIndex].value);
        focusedOptionIndex.value = optionIndex;
      }
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (focusedOptionIndex.value > 0) {
        focusedOptionIndex.value--;
        updateVisualFocus();
      }
      break;
    case 'ArrowDown':
      event.preventDefault();
      if (focusedOptionIndex.value < processedOptions.length - 1) {
        focusedOptionIndex.value++;
        updateVisualFocus();
      }
      break;
    case 'Enter':
      event.preventDefault();
      if (processedOptions[focusedOptionIndex.value]) {
        selectOption(question.id, processedOptions[focusedOptionIndex.value].value);
      }
      break;
    case ' ':
    case 'Space':
      event.preventDefault();
      if (isStepCompleted.value) {
        handleNextClick();
      } else if (processedOptions[focusedOptionIndex.value]) {
        selectOption(question.id, processedOptions[focusedOptionIndex.value].value);
      }
      break;
    case 'Escape':
      if (currentStep.value > 0) {
        handlePreviousClick();
      }
      break;
  }
};

const getProcessedOptions = (question) => {
  if (!question.options || typeof question.options !== 'object') {
    return [];
  }

  if (Array.isArray(question.options)) {
    return question.options.map((option) => ({
      value: option["@attributes"]?.value,
      label: option["@attributes"]?.label,
    }));
  }

  return Object.keys(question.options).map((key) => ({
    value: question.options[key]["@attributes"]?.value,
    label: question.options[key]["@attributes"]?.label,
  }));
};

const selectOption = (questionId, value) => {
  store.commit("editor/updateWizardAnswer", {
    questionId,
    answer: value,
  });
};

const updateVisualFocus = () => {
  // Add visual focus indicator by adding CSS class to focused option
  nextTick(() => {
    const options = document.querySelectorAll('.option-item');
    options.forEach((option, index) => {
      option.classList.toggle('keyboard-focused', index === focusedOptionIndex.value);
    });
  });
};

// Add global event listener
onMounted(() => {
  window.addEventListener('keydown', handleKeyPress);

  // Note: Step restoration is handled by the watcher on steps.value.length
  // to avoid race conditions where steps haven't loaded yet
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress);
});

// Function to calculate the current step based on answered questions
const calculateCurrentStep = () => {
  if (steps.value.length === 0) return 0;

  // Find the first step that should be displayed and doesn't have all answers
  for (let i = 0; i < steps.value.length; i++) {
    if (shouldStepBeDisplayed(i)) {
      const stepId = steps.value[i]?.id;

      // Check if this step has any unanswered questions
      const hasUnansweredQuestions = steps.value[i].subSteps.some(subStep =>
        subStep.questions.some(question => {
          const answer = answers.value.find(a => a.questionId === question.id);
          return !answer || !answer.answer || answer.answer.trim() === '';
        })
      );

      if (hasUnansweredQuestions) {
        return i;
      }
    }
  }

  // If all questions are answered, return the last visible step
  for (let i = steps.value.length - 1; i >= 0; i--) {
    if (shouldStepBeDisplayed(i)) {
      return i;
    }
  }

  return 0; // Fallback to first step
};

// Temporarily remove the watcher - will add it after function definitions
const actionTaken = ref(false);
let actionTimer = null;
const isStepCompleted = computed(() => {
  const currentStepId = steps.value[currentStep.value]?.id;
  return answers.value
    .filter((answer) => answer.stepId === currentStepId)
    .every((answer) => !!answer.answer);
});

// Calculate visible steps (those that meet their conditions)
const visibleSteps = computed(() => {
  return steps.value.filter((_, index) => shouldStepBeDisplayed(index));
});

// Get the current step position among visible steps only
const currentVisibleStepIndex = computed(() => {
  let visibleIndex = 0;
  for (let i = 0; i < steps.value.length; i++) {
    if (shouldStepBeDisplayed(i)) {
      visibleIndex++;
      if (i === currentStep.value) {
        return visibleIndex; // Return 1-based index
      }
    }
  }
  return 1; // Default to 1 if something goes wrong
});

const progress = computed(() => (currentVisibleStepIndex.value / visibleSteps.value.length) * 100);

// Calculate the starting question number for the current step
const currentStepStartingQuestionNumber = computed(() => {
  let questionCount = 0;

  // Count all questions from previous steps
  for (let i = 0; i < currentStep.value; i++) {
    if (shouldStepBeDisplayed(i)) {
      const step = steps.value[i];
      questionCount += step.subSteps.reduce((total, subStep) => total + subStep.questions.length, 0);
    }
  }

  return questionCount + 1; // Add 1 for 1-based indexing
});

const documentId = computed(() => store.state.editor.documentId);
const isMobile = useIsMobile();

const handleGTM = () => {
  useGTM.pushEvent({
    event: "document_wizzard",
    wizzard_step: currentStep.value + 1,
    document_template: store.state.editor.templateId,
  });
};

const handleNextClick = () => {
  actionTaken.value = true;
  clearTimeout(actionTimer);

  axios.patch(
    `api/documents/${documentId.value}/meta/${answers.value[currentStep.value].questionId}/answer`,
    {
      answer: answers.value[currentStep.value].answer,
    },
  );

  handleGTM();
  currentStep.value = getNextStepIndex();
  if (currentStep.value === null) {
    emit("completeWizard");
  }
};

const getNextStepIndex = () => {
  for (let i = currentStep.value + 1; i < steps.value.length; i++) {
    if (shouldStepBeDisplayed(i)) {
      return i;
    }
  }
  return null;
};

const getPreviousStepIndex = () => {
  for (let i = currentStep.value - 1; i >= 0; i--) {
    if (shouldStepBeDisplayed(i)) {
      return i;
    }
  }
  return currentStep.value;
};

const handlePreviousClick = () => {
  actionTaken.value = true;
  clearTimeout(actionTimer);
  currentStep.value = getPreviousStepIndex();
};

const handleSave = () => {
  actionTaken.value = true;
  clearTimeout(actionTimer);
  axios.patch(
    `api/documents/${documentId.value}/meta/${answers.value[currentStep.value].questionId}/answer`,
    {
      answer: answers.value[currentStep.value].answer,
    },
  );
  handleGTM();
  emit("completeWizard");
};

const shouldStepBeDisplayed = (stepNumber) => {
  const step = steps.value[stepNumber];

  if (step.conditions.length === 0) {
    return true;
  }

  const conditions = step.conditions;
  const conditionJoiner = step.conditions_joiner;

  const compareFunction = (condition) => {
    const answer = answers.value.find((a) => a.questionId === condition.question_id);
    return answer && answer.answer === condition.value;
  };

  if (conditionJoiner === "AND" || !conditionJoiner) {
    return conditions.every(compareFunction);
  } else if (conditionJoiner === "OR") {
    return conditions.some(compareFunction);
  } else {
    return false;
  }
};

watch(
  () => currentStep.value,
  (newStep, oldStep) => {
    // Skip if currentStep is null (not yet initialized)
    if (newStep === null) {
      return;
    }

    const stepId = steps.value[currentStep.value]?.id || null;

    if (stepId) {
      store.commit("editor/setWizardCurrentStep", stepId);

      // Reset focused option index when changing steps
      focusedOptionIndex.value = -1;

      // Update visual focus after DOM updates
      nextTick(() => {
        updateVisualFocus();
      });

      actionTaken.value = false;
      clearTimeout(actionTimer);
      actionTimer = setTimeout(() => {
        if (!actionTaken.value) {
          emit("noActionTaken", { id: stepId, type: "wizard" });
        }
      }, 60000 * 7); // 7 minutes
    }
  },
  { immediate: true },
);

watch(
  () => answers.value,
  async () => {
    const isLastStep = currentStep.value + 1 === steps.value.length;

    await nextTick();
    const button = isLastStep ? buttonREF.save : buttonREF.next;
    if (button.value?.$el) {
      button.value.$el.focus();
    }
  },
  { deep: true },
);

// Step restoration watcher - placed after all function definitions
watch(
  () => steps.value.length,
  (stepLength) => {
    if (stepLength > 0) {
      // Try to restore from stored step first
      const storedStepId = store.state.editor.wizard.currentStep;

      if (storedStepId) {
        const stepIndex = steps.value.findIndex(step => step.id === storedStepId);

        // If we found the stored step and it should be displayed, restore to it
        if (stepIndex !== -1 && stepIndex < steps.value.length && shouldStepBeDisplayed(stepIndex)) {
          currentStep.value = stepIndex;

          // Focus the wizard for keyboard navigation
          nextTick(() => {
            const wizardElement = document.querySelector('.document-questions-wizard');
            if (wizardElement) {
              wizardElement.focus();
            }
          });
          return;
        }
      }

      // Fallback: find the first step with unanswered questions
      for (let i = 0; i < steps.value.length; i++) {
        if (shouldStepBeDisplayed(i)) {
          const stepId = steps.value[i]?.id;
          const hasUnansweredQuestion = steps.value[i].subSteps.some(subStep =>
            subStep.questions.some(question => {
              const answer = answers.value.find(a => a.questionId === question.id);
              return !answer || !answer.answer || answer.answer.trim() === '';
            })
          );

          if (hasUnansweredQuestion) {
            currentStep.value = i;

            // Focus the wizard for keyboard navigation
            nextTick(() => {
              const wizardElement = document.querySelector('.document-questions-wizard');
              if (wizardElement) {
                wizardElement.focus();
              }
            });
            return;
          }
        }
      }

      // If all answered, stay on last visible step
      for (let i = steps.value.length - 1; i >= 0; i--) {
        if (shouldStepBeDisplayed(i)) {
          currentStep.value = i;
          break;
        }
      }

      // Focus the wizard for keyboard navigation
      nextTick(() => {
        const wizardElement = document.querySelector('.document-questions-wizard');
        if (wizardElement) {
          wizardElement.focus();
        }
      });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="document-questions-wizard" @keydown="handleKeyPress" tabindex="0">
    <!-- Compact Question Card -->
    <div class="question-container">
      <transition name="slide" mode="out-in">
        <div :key="currentStep" class="question-card">
          <!-- Compact Progress at top -->
          <div class="compact-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progress + '%' }"></div>
            </div>
          </div>

          <!-- Question Content -->
          <DocumentQuestionsStep
            v-if="steps[currentStep]"
            :step="steps[currentStep]"
            :starting-question-number="currentStepStartingQuestionNumber"
          />

          <!-- Inline Navigation -->
          <div class="inline-navigation">
            <button
              v-if="currentStep > 0"
              @click="handlePreviousClick"
              class="nav-button nav-button-secondary"
            >
              <v-icon size="16">mdi-chevron-left</v-icon>
              {{ $t("common.back") }}
            </button>

            <div class="spacer"></div>

            <button
              v-if="currentStep < steps.length - 1"
              ref="next"
              @click="handleNextClick"
              :disabled="!isStepCompleted"
              class="nav-button nav-button-primary"
            >
              {{ $t("common.next") }}
              <v-icon size="16">mdi-chevron-right</v-icon>
            </button>

            <button
              v-else
              ref="save"
              @click="handleSave"
              :disabled="!isStepCompleted"
              class="nav-button nav-button-primary"
            >
              {{ $t("common.next") }}
              <v-icon size="16">mdi-chevron-right</v-icon>
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
.document-questions-wizard {
  max-width: 600px;
  margin: 0 auto;
  padding: 0;
  outline: none;

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
    padding: 0 0.25rem;
  }
}

.question-container {
  margin-bottom: 0.5rem;
}

.question-card {
  background: white;
  border-radius: 16px;
  padding: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #E2E8F0;
}

.compact-progress {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #F1F5F9;

  .progress-counter {
    font-family: 'Archivo', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #64748B;
    white-space: nowrap;
  }

  .progress-bar {
    flex: 1;
    height: 6px;
    background: #F1F5F9;
    border-radius: 3px;
    overflow: hidden;

    .progress-fill {
      height: 100%;
      background: linear-gradient(135deg, #3B82F6 0%, #1E3A8A 100%);
      border-radius: 3px;
      transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        animation: progress-shine 2s infinite;
      }
    }

    @keyframes progress-shine {
      0% {
        left: -100%;
      }
      100% {
        left: 100%;
      }
    }
  }
}

.inline-navigation {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid #F1F5F9;

  .spacer {
    flex: 1;
  }
}

.nav-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  border-radius: 100px;
  font-family: 'Archivo', sans-serif;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 36px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
}

.nav-button-primary {
  background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 35, 239, 0.2);

  &:not(:disabled):hover {
    box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25);
  }
}

.nav-button-secondary {
  background: #F8FAFC;
  color: #64748B;
  border: 1px solid #E2E8F0;

  &:hover {
    background: #F1F5F9;
    color: #475569;
  }
}

// Slide transition
.slide-enter-active,
.slide-leave-active {
  transition: all 0.25s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

@media (max-width: 768px) {
  .question-card {
    padding: 0.75rem;
    border-radius: 8px;
    margin: 0 0.25rem;
  }

  .compact-progress {
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
  }

  .inline-navigation {
    margin-top: 0.75rem;
    padding-top: 0.5rem;
    gap: 0.5rem;
  }

  .nav-button {
    padding: 8px 12px;
    font-size: 14px;
    min-height: 36px;
    border-radius: 100px;
  }
}
</style>